import { type AxiosResponse, isAxiosError } from 'axios';

import type { components } from '~/common/types/kpc.d';

import { downloadBlob, getBlobFileName } from '@/shared/utils/fileHelpers';
import createRestClient from '../restClient';

type DocTypeTemplateResponse =
  components['schemas']['KeypointConnect.Messaging.API.MessageTemplates.V1.DTOs.Responses.DocTypeTemplateResponse'];
type DocTypeTemplateContentResponse =
  components['schemas']['KeypointConnect.Messaging.API.MessageTemplates.V1.DTOs.Responses.Abstract.IDocTypeTemplateContentResponse'];
type DocTypeTemplateValidationErrorResponse =
  components['schemas']['KeypointConnect.Messaging.API.MessageTemplates.V1.DTOs.Responses.DocTypeTemplateValidationErrorResponse'];
type DocTypeTemplateValidationProblemResponse =
  components['schemas']['KeypointConnect.Messaging.API.MessageTemplates.V1.DTOs.Responses.DocTypeTemplateValidationProblemResponse'];

export type DocTypeTemplate = DocTypeTemplateResponse;
export type DocTypeTemplateContent = DocTypeTemplateContentResponse;
export type DocTypeTemplateDocType = DocTypeTemplate['docType'];
export type DocTypeTemplateLanguage = DocTypeTemplateContent['language'];
export type DocTypeTemplateValidationError = DocTypeTemplateValidationErrorResponse;
export type DocTypeTemplateValidationProblem = DocTypeTemplateValidationProblemResponse;

export const DOC_TYPE_TEMPLATE_LANGUAGES: DocTypeTemplateLanguage[] = [
  'Dutch',
  'French',
  'English',
];

export type UpdateDocTypeTemplatesInput = {
  docType: DocTypeTemplateDocType;
  files: {
    language: DocTypeTemplateLanguage;
    file: File;
  }[];
};

export type DownloadDocTypeTemplateInput = {
  docType: DocTypeTemplateDocType;
  language: DocTypeTemplateLanguage;
};

export type GenerateTemporaryDocTypeTemplatePreviewInput = DownloadDocTypeTemplateInput & {
  file: File;
};

export type DocTypeTemplatePreview = {
  blob: Blob;
  fileName: string;
};

export type UpdateUseLatestKeypointTemplateInput = {
  docType: DocTypeTemplateDocType;
  useLatestKeypointTemplate: boolean;
};

const client = createRestClient();
const rawClient = createRestClient(undefined, true);
const DOC_TYPE_TEMPLATE_VALIDATION_STATUS = 422;

const getDocTypeTemplateUrl = (
  docType: DocTypeTemplateDocType,
  language?: DocTypeTemplateLanguage,
) =>
  language
    ? `messagetemplate/doc-types/${docType}/${language}`
    : `messagetemplate/doc-types/${docType}`;

const isDocTypeTemplateValidationProblem = (
  value: unknown,
): value is DocTypeTemplateValidationProblem =>
  typeof value === 'object' &&
  value !== null &&
  Array.isArray((value as DocTypeTemplateValidationProblem).errors);

const getDocTypeTemplateValidationProblem = (error: unknown) => {
  if (!isAxiosError<DocTypeTemplateValidationProblem>(error)) {
    return null;
  }

  const { response } = error;

  if (!response || response.status !== DOC_TYPE_TEMPLATE_VALIDATION_STATUS) {
    return null;
  }

  const { data } = response;

  if (!isDocTypeTemplateValidationProblem(data)) {
    return null;
  }

  return data;
};

const normalizeDocTypeTemplateBlobValidationError = async (error: unknown) => {
  if (!isAxiosError<Blob>(error)) {
    return error;
  }

  const { response } = error;
  if (!response || response.status !== DOC_TYPE_TEMPLATE_VALIDATION_STATUS) {
    return error;
  }

  const { data } = response;
  if (!(data instanceof Blob)) {
    return error;
  }

  try {
    const parsedData = JSON.parse(await data.text()) as unknown;

    if (isDocTypeTemplateValidationProblem(parsedData)) {
      (response as AxiosResponse<unknown>).data = parsedData;
    }
  } catch {
    return error;
  }

  return error;
};

const getFileName = (response: AxiosResponse<Blob>, fallbackName: string) => {
  const contentDisposition = response.headers['content-disposition'];
  const fileName = getBlobFileName(contentDisposition);

  return fileName === 'default_fileName' ? fallbackName : fileName;
};

const downloadFile = async (url: string, fallbackName: string) => {
  const response = await rawClient.get<Blob>(url, { responseType: 'blob' });
  await downloadBlob(response.data, getFileName(response, fallbackName));
};

const getPreviewFile = (response: AxiosResponse<Blob>, fallbackName: string) => ({
  blob: response.data,
  fileName: getFileName(response, fallbackName),
});

const getFile = async (url: string, fallbackName: string): Promise<DocTypeTemplatePreview> => {
  const response = await rawClient.get<Blob>(url, { responseType: 'blob' });
  return getPreviewFile(response, fallbackName);
};

const postFile = async (url: string, fallbackName: string): Promise<DocTypeTemplatePreview> => {
  try {
    const response = await rawClient.post<Blob>(url, undefined, {
      responseType: 'blob',
    });

    return getPreviewFile(response, fallbackName);
  } catch (error) {
    const normalizedError = await normalizeDocTypeTemplateBlobValidationError(error);
    throw normalizedError;
  }
};

const postFormDataFile = async (
  url: string,
  formData: FormData,
  fallbackName: string,
): Promise<DocTypeTemplatePreview> => {
  try {
    const response = await rawClient.post<Blob>(url, formData, {
      responseType: 'blob',
    });

    return getPreviewFile(response, fallbackName);
  } catch (error) {
    const normalizedError = await normalizeDocTypeTemplateBlobValidationError(error);
    throw normalizedError;
  }
};

const getDocTypeTemplates = (): Promise<DocTypeTemplate[]> =>
  client.get('messagetemplate/doc-types');

const addDefaultDocTypes = () => client.post('messagetemplate/doc-types/add-default');

const downloadDocTypeTemplate = ({ docType, language }: DownloadDocTypeTemplateInput) =>
  downloadFile(
    `${getDocTypeTemplateUrl(docType, language)}/template/download`,
    `${docType}_${language}.docx`,
  );

const downloadDefaultDocTypeTemplate = ({ docType, language }: DownloadDocTypeTemplateInput) =>
  downloadFile(
    `${getDocTypeTemplateUrl(docType, language)}/default-template/download`,
    `${docType}_${language}_default.docx`,
  );

const getDocTypeTemplatePreview = ({ docType, language }: DownloadDocTypeTemplateInput) =>
  getFile(`${getDocTypeTemplateUrl(docType, language)}/preview`, `${docType}_${language}.pdf`);

const generateDocTypeTemplatePreview = ({ docType, language }: DownloadDocTypeTemplateInput) =>
  postFile(
    `${getDocTypeTemplateUrl(docType, language)}/preview/generate`,
    `${docType}_${language}.pdf`,
  );

const generateTemporaryDocTypeTemplatePreview = ({
  docType,
  file,
  language,
}: GenerateTemporaryDocTypeTemplatePreviewInput) => {
  const formData = new FormData();
  formData.append('file', file);

  return postFormDataFile(
    `${getDocTypeTemplateUrl(docType, language)}/preview/generate-temporary`,
    formData,
    `${docType}_${language}.pdf`,
  );
};

const updateDocTypeTemplates = async ({ docType, files }: UpdateDocTypeTemplatesInput) => {
  const formData = new FormData();
  files.forEach(({ language, file }) => formData.append(language, file));

  await client.put(`${getDocTypeTemplateUrl(docType)}/templates`, formData);
};

const resetDocTypeTemplate = (docType: DocTypeTemplateDocType) =>
  client.post(`${getDocTypeTemplateUrl(docType)}/reset`);

const updateUseLatestKeypointTemplate = ({
  docType,
  useLatestKeypointTemplate,
}: UpdateUseLatestKeypointTemplateInput) =>
  client.put(`${getDocTypeTemplateUrl(docType)}/use-latest-keypoint-template`, {
    useLatestKeypointTemplate,
  });

export default {
  addDefaultDocTypes,
  downloadDefaultDocTypeTemplate,
  downloadDocTypeTemplate,
  generateDocTypeTemplatePreview,
  generateTemporaryDocTypeTemplatePreview,
  getDocTypeTemplatePreview,
  getDocTypeTemplates,
  getDocTypeTemplateValidationProblem,
  resetDocTypeTemplate,
  updateDocTypeTemplates,
  updateUseLatestKeypointTemplate,
};
